/**
 * 通知先メール管理ページ (SBI-6.5)。
 *
 * 現場 WG の WebApp 上で、AlertRecipient テーブル (所属区所単位の通知メール宛先) を
 * 追加 / 削除する GUI。SBI-6 の CRUD API (`/alert-recipients`) を叩く。
 *
 * 機能:
 *   - 所属区所 (depot_id = 所属区所名。例: "さいたま車両") ごとに宛先をグルーピングして一覧表示
 *   - 追加フォーム: depot_id (select / depot-master.json の value + 既存登録値) + email + label(任意)
 *     + 通知種別 (notify_types: アラート通知 / 各画面の変更通知。1 つ以上必須)
 *     + 対象線区 (route_ids: 放送地点 / 任意テキストの変更通知を受け取る線区。未選択 = 全線区)
 *   - 各宛先行に削除ボタン (ConfirmDialog で確認 → DELETE)
 *   - 読み込み中 / エラー表示 / 再読み込み
 *
 * 【「区所」⇔「線区」の関連付けについて】
 *   放送地点の変更通知は「変更された線区が関連する区所の宛先にのみ送る」要件だが、
 *   本システムに区所⇔線区の関連マスタは存在しない
 *   (`depot-master.json` は編成コード → 区所名の対応のみ)。そこで
 *   **宛先レコード自身に購読線区 (route_ids) を複数選択で持たせる** 方式を採る。
 *   設計根拠の詳細は `lambda/control-panel-api/master_change_notify.py` を参照。
 */
import { Spinner } from '../components/Spinner';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { AlertRecipientsClient } from '../api/alertRecipients';
import { ApiError } from '../api/common';
import type { AlertRecipient, NotifyType } from '../api/types';
import { NOTIFY_TYPES, NOTIFY_TYPE_LABELS } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { ConfirmDialog } from '../components/ConfirmDialog';
import type { AppConfig } from '../config';
import { extractDepotIds, fetchDepotMaster } from '../data/depots';
import type { RouteMaster } from '../data/routes';
import { fetchRoutesMaster } from '../data/routes';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { describeApiErrorMessage } from '../utils/apiErrorMessage';
import { formatDateTime } from '../utils/dateTime';
import { judgeRemoteChange } from '../utils/remoteChange';

interface AlertRecipientPageProps {
  config: AppConfig;
}

/** 削除確認ダイアログの対象 (depot_id + email) */
interface DeleteTarget {
  depot_id: string;
  email: string;
}

/** インライン編集中の行の状態 (depot_id は固定、email/label/notify_types を編集) */
interface EditState {
  /** 編集対象の識別 (depot_id) */
  depot_id: string;
  /** 編集開始時の email (キー変更判定 / DELETE 対象に使う) */
  originalEmail: string;
  /** 編集開始時の label (変更判定に使う) */
  originalLabel: string;
  /** 編集開始時の更新日時 (LastUpdate)。更新確定時に他者の更新 / 削除を判定する基準 (Issue #765) */
  originalUpdatedAt: string | null;
  /** 編集開始時の notify_types (変更判定に使う。正規化済みの配列) */
  originalNotifyTypes: NotifyType[];
  /** 入力中の email */
  email: string;
  /** 入力中の label */
  label: string;
  /** 入力中の notify_types (1 つ以上必須) */
  notifyTypes: NotifyType[];
  /** 編集開始時の route_ids (変更判定に使う。正規化済みの配列) */
  originalRouteIds: string[];
  /** 入力中の route_ids (空配列 = 全線区) */
  routeIds: string[];
  /** 保存処理中フラグ */
  saving: boolean;
}

/** 「線区を絞らない = 全線区」を表す表示文字列 (一覧セル / 入力補助で共通利用)。 */
const ALL_ROUTES_LABEL = '全線区';

/**
 * 対象線区 (route_ids) で宛先を絞り込む通知の種類。
 *
 * ⚠️ バックエンドが `route_ids` を見る通知と一致させること
 *    (放送地点 = `master_change`、任意テキスト / 配布テンプレート = `free_text_templates`)。
 *    ここに含めない種類だけを選んだ宛先は線区を設定できず、空配列 (= 全線区) のまま
 *    すべての線区の変更通知が届いてしまう (Issue #893)。
 */
const ROUTE_SCOPED_NOTIFY_TYPES: readonly NotifyType[] = ['master_change', 'free_text_templates'];

/** 通知の種類に、対象線区で絞り込める種類が含まれるか。 */
function hasRouteScopedNotifyType(values: readonly NotifyType[]): boolean {
  return values.some((t) => ROUTE_SCOPED_NOTIFY_TYPES.includes(t));
}

/** 通知種別の集合を NOTIFY_TYPES の定義順に整えた配列で返す (比較・送信の正規形)。 */
function sortNotifyTypes(values: readonly NotifyType[]): NotifyType[] {
  const set = new Set(values);
  return NOTIFY_TYPES.filter((t) => set.has(t));
}

/** 通知種別の配列が同一内容か (順序差を無視して) 判定する。 */
function isSameNotifyTypes(a: readonly NotifyType[], b: readonly NotifyType[]): boolean {
  const sortedA = sortNotifyTypes(a);
  const sortedB = sortNotifyTypes(b);
  return sortedA.length === sortedB.length && sortedA.every((t, i) => t === sortedB[i]);
}

/**
 * 購読線区 (route_ids) をマスタの定義順に整えた配列で返す (比較・送信の正規形)。
 *
 * マスタに無い線区 (マスタから削除された等) は末尾へ回して保持する。捨ててしまうと
 * 「編集して保存しただけで購読線区が減る」事故になるため、順序だけ後ろへ寄せる。
 */
function sortRouteIds(values: readonly string[], routeOrder: readonly string[]): string[] {
  const unique = Array.from(new Set(values.filter((v) => v.trim() !== '')));
  const known = routeOrder.filter((r) => unique.includes(r));
  const unknown = unique.filter((v) => !routeOrder.includes(v));
  return [...known, ...unknown];
}

/** 購読線区の配列が同一内容か (順序差を無視して) 判定する。 */
function isSameRouteIds(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((v) => setB.has(v));
}

/**
 * 購読線区 (route_ids) を選択するドロップダウン (選択済み一覧 + 追加用プルダウン)。
 *
 * アイステイションズ路線の選択 (`IStationsLinesSelect`) や任意テキストの放送言語選択と
 * 操作を揃えるため、チェックボックスではなく「選択済みチップ + 追加プルダウン」にしている。
 *
 * ⚠️ **未選択は「全線区」** を意味する (「1 つも受け取らない」ではない)。
 * サーバ側も空配列を全線区として扱うため、UI 文言もそれに揃えて誤解を防ぐ。
 *
 * 通知種別に線区で絞り込める通知先 (放送地点 / 任意テキスト) が含まれない宛先では
 * この設定が使われないため、
 * 呼び出し側から `disabled` を渡して操作させない (値自体は保持する)。
 */
function RouteIdSelect(props: {
  /** 選択肢となる線区マスタ (取得できていない場合は空配列) */
  routes: readonly RouteMaster[];
  /** 現在値 (空配列 = 全線区) */
  value: readonly string[];
  /** 変更時のコールバック (マスタ定義順に整えた配列を渡す) */
  onChange: (next: string[]) => void;
  /** 入力を無効化するか */
  disabled?: boolean;
  /** data-testid のプレフィックス */
  idPrefix: string;
  /** スクリーンリーダ向けにどの行の設定かを補足する文字列 */
  ariaLabelSuffix?: string;
}): React.JSX.Element {
  const { routes, value, onChange, disabled, idPrefix, ariaLabelSuffix } = props;
  const routeOrder = routes.map((r) => r.route_id);
  if (routes.length === 0) {
    // 線区マスタが取得できない場合は選択肢を出せない。既存値 (全線区) の
    // ままとなる旨を明示し、「選択できないのは不具合ではない」と分かるようにする。
    return (
      <span className="field-hint" data-testid={`${idPrefix}-unavailable`}>
        線区一覧を取得できませんでした（{ALL_ROUTES_LABEL}として扱われます）
      </span>
    );
  }
  // 保存値はマスタ定義順に並べて表示する (マスタに無い値は末尾。外せるよう一覧には出す)。
  const selected = sortRouteIds(value, routeOrder);
  const addable = routes.filter((r) => !selected.includes(r.route_id));
  const suffix = ariaLabelSuffix ? ` (${ariaLabelSuffix})` : '';

  return (
    <div className="route-id-select" data-testid={`${idPrefix}-group`}>
      {selected.length > 0 ? (
        <ul className="istations-chip-list" data-testid={`${idPrefix}-list`}>
          {selected.map((routeId) => (
            <li key={routeId} data-testid={`${idPrefix}-item-${routeId}`}>
              <span className="istations-chip-list__name">
                {routeId}
                {routeOrder.includes(routeId) ? null : (
                  <span className="istations-chip-list__warn">(一覧にない線区)</span>
                )}
              </span>
              <button
                type="button"
                className="istations-chip-list__remove"
                aria-label={`${routeId} を対象線区から外す${suffix}`}
                title="この線区を外す"
                disabled={disabled}
                data-testid={`${idPrefix}-remove-${routeId}`}
                onClick={() => onChange(selected.filter((v) => v !== routeId))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <select
        aria-label={`対象線区を追加${suffix}`}
        value=""
        disabled={disabled || addable.length === 0}
        data-testid={`${idPrefix}-add-select`}
        onChange={(e) => {
          const routeId = e.target.value;
          if (!routeId || selected.includes(routeId)) return;
          onChange(sortRouteIds([...selected, routeId], routeOrder));
        }}
      >
        <option value="">
          {addable.length === 0 ? '(追加できる線区がありません)' : '＋ 線区を追加…'}
        </option>
        {addable.map((route) => (
          <option key={route.route_id} value={route.route_id}>
            {route.route_id}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * 通知種別を選択するチェックボックス群。
 *
 * 追加フォームと行のインライン編集の両方で使うため独立させる。
 * 「1 つ以上必須」の制約は呼び出し側 (canSubmit / canSaveEdit) で担保する。
 */
function NotifyTypeCheckboxes(props: {
  /** チェック状態の現在値 */
  value: readonly NotifyType[];
  /** チェック変更時のコールバック (変更後の配列を渡す) */
  onChange: (next: NotifyType[]) => void;
  /** 入力を無効化するか */
  disabled?: boolean;
  /** data-testid のプレフィックス (`${idPrefix}-${notifyType}`) */
  idPrefix: string;
  /** スクリーンリーダ向けにどの行の設定かを補足する文字列 */
  ariaLabelSuffix?: string;
}): React.JSX.Element {
  const { value, onChange, disabled, idPrefix, ariaLabelSuffix } = props;
  return (
    <div className="notify-type-checkboxes" data-testid={`${idPrefix}-group`}>
      {NOTIFY_TYPES.map((notifyType) => {
        const checked = value.includes(notifyType);
        return (
          <label key={notifyType} className="notify-type-checkboxes__item">
            <input
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={(e) => {
                const next = e.target.checked
                  ? sortNotifyTypes([...value, notifyType])
                  : value.filter((t) => t !== notifyType);
                onChange(sortNotifyTypes(next));
              }}
              aria-label={
                ariaLabelSuffix
                  ? `${NOTIFY_TYPE_LABELS[notifyType]} (${ariaLabelSuffix})`
                  : NOTIFY_TYPE_LABELS[notifyType]
              }
              data-testid={`${idPrefix}-${notifyType}`}
            />
            {NOTIFY_TYPE_LABELS[notifyType]}
          </label>
        );
      })}
    </div>
  );
}

/** 簡易メール形式チェック (バックエンドと同等の緩いチェック) */
function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** 宛先一覧を depot_id ごとにグルーピングし、depot_id 昇順で返す。 */
function groupByDepot(
  items: AlertRecipient[],
): { depotId: string; recipients: AlertRecipient[] }[] {
  const map = new Map<string, AlertRecipient[]>();
  for (const item of items) {
    const list = map.get(item.depot_id) ?? [];
    list.push(item);
    map.set(item.depot_id, list);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([depotId, recipients]) => ({
      depotId,
      recipients: recipients.slice().sort((a, b) => a.email.localeCompare(b.email)),
    }));
}

/** API クライアントの例外を画面表示用の日本語メッセージに整形する (Issue #815) */
function formatApiError(err: unknown): string {
  return describeApiErrorMessage(err);
}

export function AlertRecipientPage(props: AlertRecipientPageProps): React.JSX.Element {
  const { config } = props;
  const { getIdToken } = useAuth();

  const apiClient = useMemo(
    () => new AlertRecipientsClient({ getIdToken, config }),
    [getIdToken, config],
  );

  /**
   * 編集を破棄するときの確認と、更新確定時の他者による更新 / 削除の確認 (Issue #765)。
   * 「実行してよろしいですか」は削除のみ (削除は専用の確認ダイアログを使う)。
   */
  const { confirmDiscard, guardedUpdate, confirmDialog } = useConfirmDialog();

  const [items, setItems] = useState<AlertRecipient[]>([]);
  const [masterDepotIds, setMasterDepotIds] = useState<string[]>([]);
  // 線区マスタ (routes-master.json)。購読線区 (route_ids) の選択肢に使う。
  // 取得失敗時は空配列 = ドロップダウンを出さず「全線区」のまま扱う。
  const [masterRoutes, setMasterRoutes] = useState<RouteMaster[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  // --- 追加フォームの入力状態 ---
  const [formDepotId, setFormDepotId] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formLabel, setFormLabel] = useState('');
  // 通知種別の既定値はアラート通知のみ (従来の登録と同じ挙動になるようにする)
  const [formNotifyTypes, setFormNotifyTypes] = useState<NotifyType[]>(['alert']);
  // 購読線区の既定値は空配列 = 全線区 (絞り込みは運用者が明示的に行う)
  const [formRouteIds, setFormRouteIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // --- 削除確認ダイアログ ---
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);

  // --- インライン編集 ---
  const [edit, setEdit] = useState<EditState | null>(null);

  /** 宛先一覧を取得して state に反映する (競合防止に requestId を使う)。 */
  const reload = useCallback(async (): Promise<void> => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await apiClient.list();
      if (currentRequestId !== requestIdRef.current) return;
      setItems(res.items);
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      setErrorMessage(formatApiError(err));
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiClient]);

  // 初回マウント時に depot / 線区マスタと宛先一覧を取得する。
  useEffect(() => {
    let depotMasterActive = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- API 取得の初回ロードは正当な副作用
    void reload();
    void fetchDepotMaster()
      .then((master) => {
        if (depotMasterActive) setMasterDepotIds(extractDepotIds(master));
      })
      // depot マスタ取得失敗は致命的ではない (自由入力で継続可能) ため握りつぶす
      .catch(() => {
        if (depotMasterActive) setMasterDepotIds([]);
      });
    void fetchRoutesMaster()
      .then((master) => {
        if (depotMasterActive) setMasterRoutes(master.routes ?? []);
      })
      // 線区マスタ取得失敗も致命的ではない。空配列にすると購読線区の選択肢が
      // 出なくなるだけで、宛先の登録・編集自体は続けられる (全線区扱い)。
      .catch(() => {
        if (depotMasterActive) setMasterRoutes([]);
      });
    return () => {
      depotMasterActive = false;
      // eslint-disable-next-line react-hooks/exhaustive-deps -- ref は DOM ではなくリクエスト世代カウンタ。cleanup 時の最新値を加算して進行中の古い結果を破棄するのが意図
      ++requestIdRef.current;
    };
  }, [reload]);

  // depot_id の選択肢 = マスタ ∪ 既存登録済み depot_id (昇順ユニーク)
  const depotOptions = useMemo(() => {
    const set = new Set<string>(masterDepotIds);
    for (const item of items) set.add(item.depot_id);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [masterDepotIds, items]);

  const groups = useMemo(() => groupByDepot(items), [items]);

  // 線区マスタの定義順 (route_ids の正規形を決める基準)。
  const routeOrder = useMemo(() => masterRoutes.map((r) => r.route_id), [masterRoutes]);

  /**
   * 購読線区の表示文字列を作る (一覧セル用)。
   *
   * 空配列は `全線区` と表示する。線区で絞り込める通知先でない宛先は
   * この設定が使われないため `—` としてノイズを減らす。
   */
  const formatRouteIdsCell = useCallback(
    (r: AlertRecipient): string => {
      if (!hasRouteScopedNotifyType(r.notify_types)) return '—';
      const sorted = sortRouteIds(r.route_ids, routeOrder);
      return sorted.length === 0 ? ALL_ROUTES_LABEL : sorted.join(' / ');
    },
    [routeOrder],
  );

  const canSubmit =
    formDepotId.trim() !== '' &&
    isValidEmail(formEmail) &&
    // 通知種別は 1 つ以上必須 (どの通知も受け取らない宛先は登録意義が無い)
    formNotifyTypes.length > 0 &&
    !submitting &&
    edit === null;

  /** 追加フォームの送信 (POST → リロード)。 */
  const handleAdd = useCallback(async (): Promise<void> => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await apiClient.create({
        depot_id: formDepotId.trim(),
        email: formEmail.trim(),
        // label 省略 = put_item でクリア扱い (空文字時は意図的に省く)
        ...(formLabel.trim() ? { label: formLabel.trim() } : {}),
        notify_types: sortNotifyTypes(formNotifyTypes),
        // 空配列 (= 全線区) はサーバ側の既定と同じなので送らない。
        // 常に送ると既存 API の呼び出し形が変わり、既存テストの期待値も壊れる。
        ...(formRouteIds.length > 0 ? { route_ids: sortRouteIds(formRouteIds, routeOrder) } : {}),
      });
      // 入力欄をクリア (depot_id / 通知種別 / 対象線区は続けて追加しやすいよう残す)
      setFormEmail('');
      setFormLabel('');
      await reload();
    } catch (err) {
      setErrorMessage(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }, [
    apiClient,
    canSubmit,
    formDepotId,
    formEmail,
    formLabel,
    formNotifyTypes,
    formRouteIds,
    reload,
    routeOrder,
  ]);

  /** 削除確定 (DELETE → リロード)。 */
  const handleDeleteConfirmed = useCallback(async (): Promise<void> => {
    if (!deleteTarget || deleting) return;
    const target = deleteTarget;
    setDeleting(true);
    setErrorMessage(null);
    try {
      await apiClient.remove(target.depot_id, target.email);
      setDeleteTarget(null);
      await reload();
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // 先に他者が削除済み: 削除したい状態にはなっているのでメッセージは出さず、一覧だけ更新する (Issue #765)。
        setDeleteTarget(null);
        await reload();
        return;
      }
      // 失敗時はダイアログを維持して再試行できるようにする
      setErrorMessage(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  }, [apiClient, deleteTarget, deleting, reload]);

  /** 指定行の編集を開始する。 */
  const startEdit = useCallback(
    (r: AlertRecipient): void => {
      setErrorMessage(null);
      const notifyTypes = sortNotifyTypes(r.notify_types);
      const routeIds = sortRouteIds(r.route_ids, routeOrder);
      setEdit({
        depot_id: r.depot_id,
        originalEmail: r.email,
        originalLabel: r.label ?? '',
        originalUpdatedAt: r.updated_at,
        originalNotifyTypes: notifyTypes,
        email: r.email,
        label: r.label ?? '',
        notifyTypes,
        originalRouteIds: routeIds,
        routeIds,
        saving: false,
      });
    },
    [routeOrder],
  );

  /**
   * 編集をキャンセルする。変更した項目がある場合は、更新せずに閉じることになるので
   * 破棄の確認を挟む (Issue #765)。
   */
  const cancelEdit = useCallback((): void => {
    const dirty =
      edit !== null &&
      (edit.email.trim() !== edit.originalEmail.trim() ||
        edit.label.trim() !== edit.originalLabel.trim() ||
        !isSameNotifyTypes(edit.notifyTypes, edit.originalNotifyTypes) ||
        !isSameRouteIds(edit.routeIds, edit.originalRouteIds));
    if (dirty) {
      confirmDiscard(() => setEdit(null));
      return;
    }
    setEdit(null);
  }, [confirmDiscard, edit]);

  /** 削除確認ダイアログを閉じる。 */
  const cancelDelete = useCallback((): void => {
    setDeleteTarget(null);
  }, []);

  const canSaveEdit =
    edit !== null &&
    isValidEmail(edit.email) &&
    // 通知種別は 1 つ以上必須
    edit.notifyTypes.length > 0 &&
    !edit.saving &&
    (edit.email.trim() !== edit.originalEmail.trim() ||
      edit.label.trim() !== edit.originalLabel.trim() ||
      !isSameNotifyTypes(edit.notifyTypes, edit.originalNotifyTypes) ||
      !isSameRouteIds(edit.routeIds, edit.originalRouteIds));

  /**
   * 編集を保存する。
   *
   * - email 未変更: create で上書き (label 更新)。
   * - email 変更あり: 新 email で create (追加) → 成功後に旧 email を remove (削除)。
   *   (email は SK のためキー変更は「追加 + 旧削除」で実現する)
   */
  const executeSaveEdit = useCallback(async (): Promise<void> => {
    if (edit === null || !isValidEmail(edit.email) || edit.notifyTypes.length === 0 || edit.saving)
      return;
    const target = edit;
    const newEmail = target.email.trim();
    const originalEmail = target.originalEmail.trim();
    const emailChanged = newEmail !== originalEmail;
    const labelChanged = target.label.trim() !== target.originalLabel.trim();
    const notifyTypesChanged = !isSameNotifyTypes(target.notifyTypes, target.originalNotifyTypes);
    const routeIdsChanged = !isSameRouteIds(target.routeIds, target.originalRouteIds);
    if (!emailChanged && !labelChanged && !notifyTypesChanged && !routeIdsChanged) {
      setEdit(null);
      return;
    }
    setEdit({ ...target, saving: true });
    setErrorMessage(null);
    try {
      await apiClient.create({
        depot_id: target.depot_id,
        email: newEmail,
        // label 省略 = put_item でクリア扱い (空文字時は意図的に省く)
        ...(target.label.trim() ? { label: target.label.trim() } : {}),
        notify_types: sortNotifyTypes(target.notifyTypes),
        // 空配列 (= 全線区) は送らない。put_item は全フィールド置換なので、
        // キーを省くことが「絞り込み解除 = 全線区」を意味する。
        ...(target.routeIds.length > 0
          ? { route_ids: sortRouteIds(target.routeIds, routeOrder) }
          : {}),
      });
    } catch (err) {
      setErrorMessage(formatApiError(err));
      setEdit((prev) => (prev ? { ...prev, saving: false } : prev));
      return;
    }
    if (emailChanged) {
      // 旧 email レコードを削除 (キー変更の後始末)
      try {
        await apiClient.remove(target.depot_id, originalEmail);
      } catch {
        // 新レコードは作成済み。実態を反映するため reload してから警告を表示する
        setEdit(null);
        await reload();
        setErrorMessage(
          `新しいメールアドレスの登録は完了しましたが、旧アドレス (${originalEmail}) の削除に失敗しました。画面を確認してください。`,
        );
        return;
      }
    }
    setEdit(null);
    await reload();
  }, [apiClient, edit, reload, routeOrder]);

  /**
   * 保存ボタン: 開いてから他者が更新 / 削除していないかを確認してから保存する (Issue #765)。
   * 保存ボタンは変更があるときだけ有効なので、ここでは常に「自分の変更あり」として扱う。
   * 対象の識別は (depot_id, 編集開始時の email)。
   */
  const handleSaveEdit = useCallback((): void => {
    if (edit === null || !canSaveEdit) return;
    const target = edit;
    void guardedUpdate({
      hasChanges: true,
      fetchRemoteState: async () => {
        const res = await apiClient.list(target.depot_id);
        return judgeRemoteChange(
          target.originalUpdatedAt,
          res.items.find((r) => r.depot_id === target.depot_id && r.email === target.originalEmail),
        );
      },
      execute: () => void executeSaveEdit(),
      onDeleted: () => {
        setEdit(null);
        void reload();
      },
    });
  }, [apiClient, canSaveEdit, edit, executeSaveEdit, guardedUpdate, reload]);

  return (
    <section className="section section--page list-page" data-testid="alert-recipient-section">
      <h2>
        通知先メール管理
        {loading ? (
          <>
            <Spinner />
            (読み込み中...)
          </>
        ) : (
          ` (${items.length} 件)`
        )}
      </h2>

      {errorMessage ? (
        <div role="alert" className="error-message" data-testid="alert-recipient-error">
          {errorMessage}
        </div>
      ) : null}

      <form
        className="alert-recipient-form"
        data-testid="alert-recipient-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) void handleAdd();
        }}
      >
        <label>
          所属区所
          <input
            type="text"
            list="depot-options"
            value={formDepotId}
            onChange={(e) => setFormDepotId(e.target.value)}
            placeholder="所属区所名（例: さいたま車両）"
            required
            data-testid="form-depot-id"
          />
          <datalist id="depot-options">
            {depotOptions.map((depotId) => (
              <option key={depotId} value={depotId} />
            ))}
          </datalist>
        </label>
        <label>
          メールアドレス
          <input
            type="email"
            value={formEmail}
            onChange={(e) => setFormEmail(e.target.value)}
            placeholder="alert@example.com"
            required
            aria-invalid={formEmail !== '' && !isValidEmail(formEmail)}
            aria-describedby="form-email-hint"
            data-testid="form-email"
          />
          <span id="form-email-hint" className="field-hint">
            {formEmail !== '' && !isValidEmail(formEmail)
              ? '有効なメールアドレスを入力してください'
              : ''}
          </span>
        </label>
        <label>
          表示名 (任意)
          <input
            type="text"
            value={formLabel}
            onChange={(e) => setFormLabel(e.target.value)}
            placeholder="運用センター"
            data-testid="form-label"
          />
        </label>
        <fieldset className="notify-type-fieldset">
          <legend>通知の種類</legend>
          <NotifyTypeCheckboxes
            value={formNotifyTypes}
            onChange={setFormNotifyTypes}
            disabled={submitting}
            idPrefix="form-notify-type"
          />
          <span className="field-hint" data-testid="form-notify-type-hint">
            {formNotifyTypes.length === 0 ? '通知の種類を 1 つ以上選択してください' : ''}
          </span>
        </fieldset>
        <fieldset className="notify-type-fieldset">
          <legend>放送地点・任意テキストの変更通知の対象線区</legend>
          <RouteIdSelect
            routes={masterRoutes}
            value={formRouteIds}
            onChange={setFormRouteIds}
            // 線区で絞り込める通知先 (放送地点 / 任意テキスト) でない宛先には効かない設定なので操作させない
            disabled={submitting || !hasRouteScopedNotifyType(formNotifyTypes)}
            idPrefix="form-route-id"
          />
          <span className="field-hint" data-testid="form-route-id-hint">
            {hasRouteScopedNotifyType(formNotifyTypes)
              ? `未選択の場合は${ALL_ROUTES_LABEL}の変更通知を受け取ります`
              : '「放送地点の変更通知先」または「任意テキスト (配布テンプレートを含む) の変更通知先」を選ぶと設定できます'}
          </span>
        </fieldset>
        <button
          type="submit"
          className="primary"
          disabled={!canSubmit}
          data-testid="form-add-button"
        >
          追加
        </button>
        <button
          type="button"
          onClick={() => void reload()}
          disabled={edit !== null}
          data-testid="alert-recipient-reload-button"
        >
          再読み込み
        </button>
      </form>

      {/* 一覧だけをスクロールさせ、タイトル・追加フォーム・表の見出し行は固定する (Issue #765) */}
      <div className="list-page__body" data-testid="alert-recipient-list-body">
        {groups.length === 0 ? (
          <p className="data-table__empty" data-testid="alert-recipient-empty">
            {loading ? (
              <>
                <Spinner />
                読み込み中...
              </>
            ) : (
              '登録されている通知先がありません'
            )}
          </p>
        ) : (
          groups.map((group) => (
            <div
              key={group.depotId}
              className="alert-recipient-group"
              data-testid={`depot-group-${encodeURIComponent(group.depotId)}`}
            >
              <h3>{group.depotId}</h3>
              <div className="table-wrapper">
                <table className="data-table alert-recipient-table">
                  {/*
                    列幅を固定する (colgroup + CSS の table-layout: fixed)。
                    区所ごとに <table> を分けているため、既定の自動レイアウトでは
                    「その表に入っている値の長さ」で列幅が決まり、表ごとに
                    列の開始位置がずれてしまう。固定幅で全表を同じ位置に揃える。
                  */}
                  <colgroup>
                    <col className="alert-recipient-table__col--email" />
                    <col className="alert-recipient-table__col--label" />
                    <col className="alert-recipient-table__col--notify-types" />
                    <col className="alert-recipient-table__col--route-ids" />
                    <col className="alert-recipient-table__col--updated" />
                    <col className="alert-recipient-table__col--actions" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th scope="col">メールアドレス</th>
                      <th scope="col">表示名</th>
                      <th scope="col">通知の種類</th>
                      <th scope="col">対象線区</th>
                      <th scope="col">更新日時</th>
                      <th scope="col">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.recipients.map((r) => {
                      const rowKey = `${encodeURIComponent(r.depot_id)}#${encodeURIComponent(r.email)}`;
                      const isEditing =
                        edit !== null &&
                        edit.depot_id === r.depot_id &&
                        edit.originalEmail === r.email;
                      if (isEditing && edit) {
                        return (
                          <tr key={rowKey} data-testid={`recipient-row-${rowKey}`}>
                            <td>
                              <input
                                type="email"
                                value={edit.email}
                                onChange={(e) =>
                                  setEdit((prev) =>
                                    prev ? { ...prev, email: e.target.value } : prev,
                                  )
                                }
                                aria-label={`${r.email} のメールアドレス(編集)`}
                                aria-invalid={!isValidEmail(edit.email)}
                                disabled={edit.saving}
                                data-testid={`edit-email-${rowKey}`}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={edit.label}
                                onChange={(e) =>
                                  setEdit((prev) =>
                                    prev ? { ...prev, label: e.target.value } : prev,
                                  )
                                }
                                aria-label={`${r.email} の表示名(編集)`}
                                placeholder="運用センター"
                                disabled={edit.saving}
                                data-testid={`edit-label-${rowKey}`}
                              />
                            </td>
                            <td>
                              <NotifyTypeCheckboxes
                                value={edit.notifyTypes}
                                onChange={(next) =>
                                  setEdit((prev) => (prev ? { ...prev, notifyTypes: next } : prev))
                                }
                                disabled={edit.saving}
                                idPrefix={`edit-notify-type-${rowKey}`}
                                ariaLabelSuffix={`${r.email} の通知の種類(編集)`}
                              />
                            </td>
                            <td>
                              <RouteIdSelect
                                routes={masterRoutes}
                                value={edit.routeIds}
                                onChange={(next) =>
                                  setEdit((prev) => (prev ? { ...prev, routeIds: next } : prev))
                                }
                                disabled={
                                  edit.saving || !hasRouteScopedNotifyType(edit.notifyTypes)
                                }
                                idPrefix={`edit-route-id-${rowKey}`}
                                ariaLabelSuffix={`${r.email} の対象線区(編集)`}
                              />
                            </td>
                            <td className="alert-recipient-table__updated">
                              {formatDateTime(r.updated_at)}
                            </td>
                            <td>
                              <div className="alert-recipient-table__actions">
                                <button
                                  type="button"
                                  className="primary"
                                  onClick={handleSaveEdit}
                                  disabled={!canSaveEdit}
                                  data-testid={`save-button-${rowKey}`}
                                >
                                  {edit.saving ? (
                                    <>
                                      <Spinner />
                                      保存中...
                                    </>
                                  ) : (
                                    '保存'
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEdit}
                                  disabled={edit.saving}
                                  data-testid={`cancel-button-${rowKey}`}
                                >
                                  キャンセル
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={rowKey} data-testid={`recipient-row-${rowKey}`}>
                          <td>{r.email}</td>
                          <td>{r.label ?? '—'}</td>
                          <td data-testid={`notify-types-${rowKey}`}>
                            {sortNotifyTypes(r.notify_types)
                              .map((t) => NOTIFY_TYPE_LABELS[t])
                              .join(' / ') || '—'}
                          </td>
                          <td data-testid={`route-ids-${rowKey}`}>{formatRouteIdsCell(r)}</td>
                          <td className="alert-recipient-table__updated">
                            {formatDateTime(r.updated_at)}
                          </td>
                          <td>
                            <div className="alert-recipient-table__actions">
                              <button
                                type="button"
                                onClick={() => startEdit(r)}
                                disabled={edit !== null}
                                aria-label={`${r.depot_id} ${r.email} を編集`}
                                data-testid={`edit-button-${rowKey}`}
                              >
                                編集
                              </button>
                              <button
                                type="button"
                                className="danger"
                                onClick={() =>
                                  setDeleteTarget({ depot_id: r.depot_id, email: r.email })
                                }
                                disabled={edit !== null}
                                aria-label={`${r.depot_id} ${r.email} を削除`}
                                data-testid={`delete-button-${rowKey}`}
                              >
                                削除
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="通知先メールの削除"
        message={
          deleteTarget
            ? `以下の通知先を削除します。よろしいですか？\n所属区所: ${deleteTarget.depot_id}\nメール: ${deleteTarget.email}`
            : ''
        }
        confirmLabel="削除"
        destructive
        confirmDisabled={deleting}
        cancelDisabled={deleting}
        onConfirm={() => void handleDeleteConfirmed()}
        onCancel={cancelDelete}
      />

      {confirmDialog}
    </section>
  );
}
